import { BaseApi } from "api/BaseApi.ts";

import GetTenantsResponse = App.Http.Responses.Tenant.GetTenantsResponse;
import StoreTenantRequest = App.Http.Requests.Tenant.StoreTenantRequest;
import GetTenantResponse = App.Http.Responses.Tenant.GetTenantResponse;
import GetTenantRequest = App.Http.Requests.Tenant.GetTenantRequest;
import UpdateTenantRequest = App.Http.Requests.Tenant.UpdateTenantRequest;
import GetSlugAvailableResponse = App.Http.Responses.Tenant.GetSlugAvailableResponse;
import GetSlugAvailableRequest = App.Http.Requests.Tenant.GetSlugAvailableRequest;
import RequestAccessRequest = App.Http.Requests.Tenant.RequestAccessRequest;
import GetMyTenantsRequest = App.Http.Requests.Tenant.GetMyTenantsRequest;
import GetCatalogTenantsRequest = App.Http.Requests.Tenant.GetCatalogTenantsRequest;
import GetCatalogTenantRequest = App.Http.Requests.Tenant.GetCatalogTenantRequest;
import GetMyTenantsResponse = App.Http.Responses.Tenant.GetMyTenantsResponse;
import RequestAccessResponse = App.Http.Responses.Tenant.RequestAccessResponse;
import GetCreationStatusResponse = App.Http.Responses.Tenant.GetCreationStatusResponse;
import RequestTenantDeletionRequest = App.Http.Requests.Tenant.RequestTenantDeletionRequest;
import ConfirmTenantDeletionRequest = App.Http.Requests.Tenant.ConfirmTenantDeletionRequest;

class TenantApi extends BaseApi {
    /**
     * Get list of Tenants
     *
     * - Request: -
     * - Response: GetTenantsResponse
     */
    async index(): Promise<GetTenantsResponse> {
        return this.get<GetTenantsResponse, void>("/tenants");
    }

    /**
     * Store Tenant
     *
     * - Request: StoreTenantRequest
     * - Response: GetTenantResponse (200) or GetCreationStatusResponse (202 when async: true)
     */
    async store(request: StoreTenantRequest): Promise<GetTenantResponse | GetCreationStatusResponse> {
        return this.post<GetTenantResponse | GetCreationStatusResponse, StoreTenantRequest>("/tenants", request);
    }

    /**
     * Get specific Tenant
     *
     * - Request: { id: string }
     * - Response: GetTenantResponse
     */
    async show(tenantId: string, request?: GetTenantRequest): Promise<GetTenantResponse> {
        return this.get<GetTenantResponse, GetTenantRequest>(`/tenants/${tenantId}`, {
            params: request,
        });
    }

    /**
     * Update Tenant
     *
     * - Request: UpdateTenantRequest
     * - Response: GetTenantResponse
     */
    async update(request: UpdateTenantRequest, tenantId: string): Promise<GetTenantResponse> {
        return await this.put<GetTenantResponse, UpdateTenantRequest>(`/tenants/${tenantId}`, request);
    }

    /**
     * Delete Tenant
     *
     * - Request: { id: string }
     * - Response: void
     */
    async destroy(tenantId: string): Promise<null> {
        return this.delete<null, void>(`/tenants/${tenantId}`);
    }

    /**
     * Check slug availability
     *
     * - Request: { name: string }
     * - Response: GetSlugAvailableResponse
     */
    async slugAvailable(name: string): Promise<GetSlugAvailableResponse> {
        return this.get<GetSlugAvailableResponse, GetSlugAvailableRequest>(`/tenants/slug-available`, {
            params: { name },
        });
    }

    /**
     * Get public tenant catalog
     *
     * - Request: GetCatalogTenantsRequest
     * - Response: GetTenantsResponse
     */
    async catalog(request: GetCatalogTenantsRequest): Promise<GetTenantsResponse> {
        return this.get<GetTenantsResponse, GetCatalogTenantsRequest>("/tenants/catalog", {
            params: request,
        });
    }

    /**
     * Get a single tenant from the public catalog
     *
     * Unlike show(), this is accessible to any authenticated user regardless of membership —
     * it only requires the tenant to have show_in_public_catalog enabled.
     *
     * - Request: GetCatalogTenantRequest
     * - Response: GetTenantResponse
     */
    async catalogShow(tenantId: string, request?: GetCatalogTenantRequest): Promise<GetTenantResponse> {
        return this.get<GetTenantResponse, GetCatalogTenantRequest>(`/tenants/catalog/${tenantId}`, {
            params: request,
        });
    }

    /**
     * Get the authenticated user's access status for a tenant, regardless of public catalog
     * visibility — unlike catalogShow(), works for tenants that aren't show_in_public_catalog.
     * Used by the "no access" page to restore the Pending state after a refresh.
     *
     * - Response: GetTenantResponse
     */
    async accessStatus(tenantId: string): Promise<GetTenantResponse> {
        return this.get<GetTenantResponse, void>(`/tenants/${tenantId}/access-status`);
    }

    /**
     * Request access to a public tenant (catalog "Apply" / "Request role")
     *
     * - Request: RequestAccessRequest
     * - Response: RequestAccessResponse
     */
    async requestAccess(tenantId: string, request?: RequestAccessRequest): Promise<RequestAccessResponse> {
        return this.post<RequestAccessResponse, RequestAccessRequest>(`/tenants/${tenantId}/request-access`, request, {
            silentError: true,
        });
    }

    /**
     * Get my tenants (active programs)
     *
     * - Request: GetMyTenantsRequest
     * - Response: GetMyTenantsResponse
     */
    async myTenants(request: GetMyTenantsRequest): Promise<GetMyTenantsResponse> {
        return this.get<GetMyTenantsResponse, GetMyTenantsRequest>("/tenants/my", {
            params: request,
        });
    }

    /**
     * Get tenant creation status (for async creation polling)
     *
     * - Response: GetCreationStatusResponse
     */
    async creationStatus(tenantId: string): Promise<GetCreationStatusResponse> {
        return this.get<GetCreationStatusResponse, void>(`/tenants/${tenantId}/creation-status`);
    }

    /**
     * Request tenant deletion
     *
     * - Request: RequestTenantDeletionRequest
     * - Response: void
     */
    async requestDeletion(tenantId: string, request: RequestTenantDeletionRequest): Promise<void> {
        return this.post<void, RequestTenantDeletionRequest>(`/tenants/${tenantId}/request-deletion`, request);
    }

    /**
     * Confirm tenant deletion via signed link
     *
     * - Request: ConfirmTenantDeletionRequest
     * - Response: void
     */
    async confirmDeletion(request: ConfirmTenantDeletionRequest): Promise<void> {
        return this.post<void, never>(`/tenants/confirm-deletion`, undefined, { params: request });
    }

    /**
     * Leave a tenant
     *
     * - Response: void
     */
    async leaveTenant(tenantId: string): Promise<void> {
        return this.post<void, void>(`/users/me/leave-tenant/${tenantId}`);
    }
}

export const tenantApi = new TenantApi();
